import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { emptyProduct } from './product/emptyProduct';
import ProductBasicsFields   from './product/ProductBasicsFields';
import ProductImagesEditor   from './product/ProductImagesEditor';
import ProductVariantsEditor from './product/ProductVariantsEditor';

/**
 * Edit a product + its variants in one form.
 *
 * On save we diff variants (create new, update kept, delete removed)
 * so this file is the only place that needs to know about the
 * per-variant lifecycle. Sub-sections (basics / images / variants)
 * live in their own files under `./product/`.
 */
export default function ProductDialog({ open, onClose, brandId, editing, onSaved }) {
  const [form, setForm] = useState(emptyProduct());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...emptyProduct(),
        ...editing,
        options: editing.options || [],
        images: editing.images || [],
        variants: editing.variants && editing.variants.length > 0
          ? editing.variants
          : emptyProduct().variants,
      });
    } else {
      setForm(emptyProduct());
    }
  }, [open, editing]);

  const save = async () => {
    setSaving(true);
    try {
      const productPatch = {
        name: form.name,
        description: form.description,
        category: form.category,
        status: form.status,
        base_price: Number(form.base_price || 0),
        currency: form.currency,
        track_inventory: form.track_inventory,
        requires_shipping: form.requires_shipping,
        options: form.options,
        images: (form.images || []).filter((i) => i && i.url),
      };
      const product = editing
        ? await API.updateShopProduct({ brand_id: brandId, _id: editing._id, patch: productPatch })
        : await API.createShopProduct({ brand_id: brandId, patch: productPatch });
      const productId = product?._id || editing?._id;

      // Variant diff: update existing ones, insert new ones, delete the
      // ones the user removed from the form.
      const existingIds = (editing?.variants || []).map((v) => v._id);
      const keepIds = [];
      for (const v of form.variants) {
        const patch = {
          sku: v.sku,
          attributes: v.attributes,
          price: v.price === '' ? null : v.price,
          is_active: v.is_active !== false,
        };
        if (v._id) {
          await API.updateShopVariant({ brand_id: brandId, _id: v._id, patch });
          keepIds.push(v._id);
        } else {
          await API.addShopVariant({ brand_id: brandId, product_id: productId, patch });
        }
      }
      for (const id of existingIds) {
        if (!keepIds.includes(id)) {
          try { await API.deleteShopVariant({ brand_id: brandId, _id: id }); }
          catch (_) { /* best-effort cleanup */ }
        }
      }

      toast.success(editing ? 'Product saved' : 'Product created');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        {editing ? `Edit "${editing.name}"` : 'New product'}
        <GlassIconButton label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='12px'>
          <ProductBasicsFields   form={form} setForm={setForm} />
          <Divider />
          <ProductImagesEditor   form={form} setForm={setForm} />
          <Divider />
          <ProductVariantsEditor form={form} setForm={setForm} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving || !form.name}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
